import { useState, useEffect } from 'react';
import { Box, Flex, Input, Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as authApi from '../../api/auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';

interface PasswordResetFlowProps {
  onSuccess?: () => void;
  onBack?: () => void;
}

type Step = 'request' | 'verify' | 'reset';

const PasswordResetFlow = ({ onSuccess, onBack }: PasswordResetFlowProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleRequestReset = async () => {
    if (!email) {
      setError(t('auth.emailRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authApi.requestPasswordReset({ email });
      toast.success(t('passwordReset.resetCodeSent'));
      setStep('verify');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('passwordReset.sendResetFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError(t('verification.codeMustBe6Digits'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.verifyPasswordResetCode({ email, code });
      setResetToken(response.reset_token);
      toast.success(t('passwordReset.codeVerified'));
      setStep('reset');
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('passwordReset.invalidCode');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);

    try {
      await authApi.resendPasswordResetCode(email);
      toast.success(t('passwordReset.resetCodeSent'));
      setResendCooldown(60);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('verification.resendFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required(t('auth.passwordRequired'))
      .min(8, t('auth.passwordMinLength')),
    confirmPassword: Yup.string()
      .required(t('auth.confirmPasswordRequired'))
      .oneOf([Yup.ref('newPassword')], t('auth.passwordsMustMatch')),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      try {
        await authApi.resetPassword({
          reset_token: resetToken,
          new_password: values.newPassword,
        });
        toast.success(t('passwordReset.resetSuccess'));
        onSuccess?.();
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || t('passwordReset.resetFailed');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (step === 'request') {
    return (
      <Flex direction="column" rowGap="24px">
        <Flex direction="column" rowGap="8px">
          <Box fontSize="24px" fontWeight="500" color="neutral.100">
            {t('passwordReset.resetPassword')}
          </Box>
          <Box fontSize="14px" fontWeight="400" color="neutral.400">
            {t('passwordReset.enterEmailForReset')}
          </Box>
        </Flex>

        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.email')}
            </Box>
            <Box textAlign="center" color="error.600" fontSize="14px" fontWeight="300">
              {error}
            </Box>
          </Flex>
          <Input
            type="email"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('auth.enterEmail')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
          />
        </Flex>

        <Flex alignItems="center">
          <Button
            height="fit-content"
            width="100%"
            py="8px"
            fontWeight="500"
            fontSize="14px"
            color="neutral.900"
            backgroundColor="neutral.100"
            loading={isLoading}
            disabled={isLoading || !email}
            onClick={handleRequestReset}
          >
            {t('passwordReset.sendResetCode')}
          </Button>
        </Flex>

        {onBack && (
          <Flex justifyContent="center">
            <Text
              as="button"
              fontSize="14px"
              fontWeight="400"
              color="neutral.400"
              textDecoration="underline"
              cursor="pointer"
              bg="none"
              border="none"
              onClick={onBack}
              _hover={{ color: 'neutral.300' }}
            >
              {t('passwordReset.backToLogin')}
            </Text>
          </Flex>
        )}
      </Flex>
    );
  }

  if (step === 'verify') {
    return (
      <Flex direction="column" rowGap="24px">
        <Flex direction="column" rowGap="8px">
          <Box fontSize="24px" fontWeight="500" color="neutral.100">
            {t('passwordReset.enterResetCode')}
          </Box>
          <Box fontSize="14px" fontWeight="400" color="neutral.400">
            {t('verification.codeSentTo', { email })}
          </Box>
        </Flex>

        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('passwordReset.resetCode')}
            </Box>
            <Box textAlign="center" color="error.600" fontSize="14px" fontWeight="300">
              {error}
            </Box>
          </Flex>
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('verification.enterCode')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={code}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setCode(value);
              setError(null);
            }}
          />
        </Flex>

        <Flex alignItems="center">
          <Button
            height="fit-content"
            width="100%"
            py="8px"
            fontWeight="500"
            fontSize="14px"
            color="neutral.900"
            backgroundColor="neutral.100"
            loading={isLoading}
            disabled={isLoading || code.length !== 6}
            onClick={handleVerifyCode}
          >
            {t('passwordReset.verifyCode')}
          </Button>
        </Flex>

        <Flex justifyContent="center" alignItems="center" columnGap="4px">
          <Box fontSize="14px" fontWeight="400" color="neutral.400">
            {t('verification.didntReceiveCode')}
          </Box>
          <Text
            as="button"
            fontSize="14px"
            fontWeight="500"
            color={resendCooldown > 0 ? 'neutral.500' : 'neutral.200'}
            textDecoration="underline"
            cursor={resendCooldown > 0 ? 'not-allowed' : 'pointer'}
            bg="none"
            border="none"
            onClick={resendCooldown > 0 || isResending ? undefined : handleResendCode}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            _hover={resendCooldown > 0 ? {} : { color: 'neutral.100' }}
          >
            {resendCooldown > 0 ? t('verification.resendIn', { seconds: resendCooldown }) : t('verification.resend')}
          </Text>
        </Flex>

        <Flex justifyContent="center">
          <Text
            as="button"
            fontSize="14px"
            fontWeight="400"
            color="neutral.400"
            textDecoration="underline"
            cursor="pointer"
            bg="none"
            border="none"
            onClick={() => setStep('request')}
            _hover={{ color: 'neutral.300' }}
          >
            {t('passwordReset.changeEmail')}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Box as="form" onSubmit={formik.handleSubmit as any}>
      <Flex direction="column" rowGap="24px">
        <Flex direction="column" rowGap="8px">
          <Box fontSize="24px" fontWeight="500" color="neutral.100">
            {t('passwordReset.setNewPassword')}
          </Box>
          <Box fontSize="14px" fontWeight="400" color="neutral.400">
            {t('passwordReset.enterNewPassword')}
          </Box>
        </Flex>

        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('passwordReset.newPassword')}
            </Box>
            <Box textAlign="center" color="error.600" fontSize="14px" fontWeight="300">
              {formik.touched.newPassword && formik.errors.newPassword ? formik.errors.newPassword : null}
            </Box>
          </Flex>
          <Input
            type="password"
            name="newPassword"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('passwordReset.enterNewPasswordPlaceholder')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        <Flex direction="column" rowGap="8px">
          <Flex justifyContent="space-between">
            <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
              {t('auth.confirmPassword')}
            </Box>
            <Box textAlign="center" color="error.600" fontSize="14px" fontWeight="300">
              {formik.touched.confirmPassword && formik.errors.confirmPassword ? formik.errors.confirmPassword : null}
            </Box>
          </Flex>
          <Input
            type="password"
            name="confirmPassword"
            p="10px"
            fontWeight="400"
            fontSize="14px"
            lineHeight="150%"
            placeholder={t('passwordReset.confirmNewPassword')}
            color="neutral.300"
            borderWidth="1px"
            borderColor="neutral.700"
            backgroundColor="neutral.800"
            borderRadius="8px"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Flex>

        <Flex alignItems="center">
          <Button
            type="submit"
            height="fit-content"
            width="100%"
            py="8px"
            fontWeight="500"
            fontSize="14px"
            color="neutral.900"
            backgroundColor="neutral.100"
            loading={isLoading}
            disabled={isLoading || !formik.isValid || !formik.dirty}
          >
            {t('passwordReset.resetPassword')}
          </Button>
        </Flex>

        {error && (
          <Box mt="8px" textAlign="center" color="error.600" fontSize="14px" fontWeight="400">
            {error}
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default PasswordResetFlow;
